import { forwardRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { useIsAdmin } from "@/hooks/useUserRole";
import SmartAccessScreen from "@/components/onboarding/SmartAccessScreen";
import OrgOnboardingFlow from "@/components/onboarding/OrgOnboardingFlow";
import { useOrgLaunchProgress } from "@/hooks/useOrgLaunchProgress";

const ProtectedRoute = forwardRef<HTMLDivElement, { children: React.ReactNode }>(({ children }, ref) => {
  const { user, loading: authLoading } = useAuth();
  const { orgId, loading: orgLoading } = useOrg();
  const { isMasterAdmin, isAssociationAdmin, isLoading: roleLoading, role } = useIsAdmin();
  const { data: launchProgress, isLoading: launchLoading } = useOrgLaunchProgress();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  // Also treat as loading if user exists but role query hasn't resolved yet
  const stillResolvingRole = !!user && !roleLoading && role === undefined;

  if (authLoading || orgLoading || roleLoading || stillResolvingRole) {
    return (
      <div ref={ref} className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Master admins and association admins always get through (they can switch orgs)
  if (isMasterAdmin || isAssociationAdmin) {
    return <div ref={ref}>{children}</div>;
  }

  // No org assigned — show Smart Access Screen
  if (!orgId) {
    return <SmartAccessScreen />;
  }

  // Org exists but onboarding not complete — show onboarding flow
  // Only show if launch progress has NOT been completed and not dismissed
  if (!launchLoading && !onboardingDismissed && launchProgress === null) {
    // No launch progress exists yet — this is a brand new org admin
    // Show onboarding only for non-master-admin users
    return (
      <OrgOnboardingFlow onComplete={() => setOnboardingDismissed(true)} />
    );
  }

  if (!launchLoading && !onboardingDismissed && launchProgress && !launchProgress.is_complete) {
    return (
      <OrgOnboardingFlow onComplete={() => setOnboardingDismissed(true)} />
    );
  }

  return <div ref={ref}>{children}</div>;
});

ProtectedRoute.displayName = "ProtectedRoute";

export default ProtectedRoute;
